import { Controller } from "@hotwired/stimulus"

// Autoboost show page, Ads tab — filter chips, sort, and per-ad error panels.
//
// Mounted OUTSIDE the ads turbo-frame on purpose: ads_poller swaps the frame's
// innerHTML on every Mercure ping, which disconnects and reconnects every
// target below. The chosen filter, the sort, and any error panel the user
// opened or closed live on this controller and are re-applied from the
// *TargetConnected callbacks, so a live refresh never resets the view.
//
// State is set up in initialize(): Stimulus fires target callbacks before
// connect().
export default class extends Controller {
  static targets = ["chip", "sort", "list", "group", "errorPanel"]

  initialize() {
    this.filter = "all"
    this.sortKey = "attention"
    this.errorOverrides = new Map()
    this.applyQueued = false
  }

  connect() {
    console.log(`[ads-section] connect groups=${this.groupTargets.length}`)
    this.apply()
  }

  chooseFilter(event) {
    this.filter = event.currentTarget.dataset.filter || "all"
    console.log(`[ads-section] filter=${this.filter}`)
    this.apply()
  }

  sort(event) {
    this.sortKey = event.currentTarget.value || "attention"
    console.log(`[ads-section] sort=${this.sortKey}`)
    this.apply()
  }

  toggleError(event) {
    const button = event.currentTarget
    const panel = document.getElementById(button.getAttribute("aria-controls"))
    if (!panel) return

    const open = panel.classList.contains("hidden")
    this.errorOverrides.set(panel.dataset.adId, open)
    this.setPanel(panel, open)
  }

  chipTargetConnected() {
    this.queueApply()
  }

  groupTargetConnected() {
    this.queueApply()
  }

  sortTargetConnected() {
    this.queueApply()
  }

  errorPanelTargetConnected(panel) {
    const open = this.errorOverrides.get(panel.dataset.adId)
    if (open !== undefined) this.setPanel(panel, open)
  }

  // --- Private ---

  queueApply() {
    if (this.applyQueued) return
    this.applyQueued = true
    queueMicrotask(() => {
      this.applyQueued = false
      this.apply()
    })
  }

  apply() {
    // A reload can drop the chip the user had chosen (its count fell to zero).
    const filters = this.chipTargets.map((chip) => chip.dataset.filter)
    if (!filters.includes(this.filter)) this.filter = "all"

    this.chipTargets.forEach((chip) => {
      if (chip.dataset.filter === this.filter) {
        chip.setAttribute("data-active", "true")
      } else {
        chip.removeAttribute("data-active")
      }
    })
    if (this.hasSortTarget && this.sortTarget.value !== this.sortKey) this.sortTarget.value = this.sortKey

    let visible = 0
    this.groupTargets.forEach((group) => {
      const match = this.filter === "all" || group.dataset.bucket === this.filter
      group.classList.toggle("hidden", !match)
      if (match) visible += 1
    })

    this.reorder()
    console.log(`[ads-section] apply filter=${this.filter} sort=${this.sortKey} visible=${visible}/${this.groupTargets.length}`)
  }

  // Only moves nodes when the order actually changes — moving a target fires
  // its disconnect/connect callbacks, which would otherwise queue apply forever.
  reorder() {
    if (!this.hasListTarget) return

    const current = this.groupTargets.filter((group) => group.parentElement === this.listTarget)
    const sorted = [...current].sort(this.comparator())
    if (sorted.every((group, index) => group === current[index])) return

    sorted.forEach((group) => this.listTarget.appendChild(group))
  }

  comparator() {
    const num = (el, key) => parseFloat(el.dataset[key]) || 0
    const byRecent = (a, b) => num(b, "createdAt") - num(a, "createdAt")

    if (this.sortKey === "recent") return byRecent
    if (this.sortKey === "spend") return (a, b) => num(b, "spend") - num(a, "spend") || byRecent(a, b)
    return (a, b) => num(a, "rank") - num(b, "rank") || byRecent(a, b)
  }

  setPanel(panel, open) {
    panel.classList.toggle("hidden", !open)
    const button = this.element.querySelector(`[aria-controls="${panel.id}"]`)
    if (button) button.setAttribute("aria-expanded", String(open))
  }
}
